import {z} from 'zod/v4';

/**
 * Realm-safe Date check. Zod v4's z.date() uses `instanceof Date` which
 * fails on Date objects created in a different V8 context (e.g. Mongoose
 * documents inside Jest's VM sandbox). Object.prototype.toString works
 * reliably across realms, while Date.prototype.getTime validates the Date
 * internal slot and rejects spoofed objects and Invalid Date instances.
 */
function isDate(val: unknown): val is Date {
  if (Object.prototype.toString.call(val) !== '[object Date]') {
    return false;
  }

  try {
    return Number.isFinite(Date.prototype.getTime.call(val));
  } catch {
    return false;
  }
}

/**
 * Schema that accepts a Date object (including cross-realm), an ISO date
 * string (YYYY-MM-DD), or an ISO datetime string, and always produces a Date.
 * Use this instead of `z.coerce.date()`, which silently coerces
 * null/undefined to epoch (1970-01-01).
 *
 * Cross-realm Date objects (e.g. Mongoose documents inside Jest's VM sandbox)
 * are handled via a realm-safe check, so callers do not need to pre-convert
 * dates to strings before parsing.
 *
 * @example
 * const Foo = z.object({createdAt: Dateish});
 * // Input type:  {createdAt: Date | string}
 * // Output type: {createdAt: Date}
 *
 * Foo.parse({createdAt: new Date()});              // {createdAt: Date} ✅
 * Foo.parse({createdAt: '2025-01-15'});            // {createdAt: Date} ✅
 * Foo.parse({createdAt: '2025-01-15T12:00:00Z'});  // {createdAt: Date} ✅
 * Foo.parse({createdAt: null});                     // ❌
 * Foo.parse({createdAt: undefined});                // ❌
 * Foo.parse({createdAt: 'not-a-date'});             // ❌
 */
export const Dateish = z
  .union([z.custom<Date>(isDate), z.iso.date(), z.iso.datetime()])
  .transform((val) => (isDate(val) ? val : new Date(val)));

// z.custom has no automatic JSON Schema representation — Zod v4 throws when
// nestjs-zod tries to build Swagger docs. _zod.toJSONSchema is checked first,
// before the type switch, so this short-circuits the throw.
(Dateish._zod as unknown as Record<string, unknown>).toJSONSchema = () =>
  z.toJSONSchema(z.iso.datetime());
